// src/theme/theme.ts
import { createTheme } from '@mui/material/styles';
import { daDK } from '@mui/material/locale';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// IMPORTANT: Do NOT use getComputedStyle for theme values.
// This causes hydration mismatches because SSR cannot access CSS variables.
// Instead, use CSS variables directly in sx props or stylesheets where needed,
// or use static fallback values here for MUI theme defaults.
// The CSS variables are applied at runtime through the stylesheets.

const theme = createTheme({
    // ========== Palette ==========
    palette: {
        primary: {
            main: 'rgba(0, 0, 0, 0.8)',
            light: '#353535',
            dark: '#101010',
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#d6c1d6',
            light: '#e6c6e6',
            dark: '#6a0080',
            contrastText: '#ffffff',
        },
        error: {
            main: '#d32f2f',
            light: '#ef5350',
            dark: '#c62828',
        },
        warning: {
            main: '#ffa726',
            light: '#ffb74d',
            dark: '#f57c00',
        },
        info: {
            main: '#0288d1',
            light: '#03a9f4',
            dark: '#01579b',
        },
        success: {
            main: '#388e3c',
            light: '#81c784',
            dark: '#2e7431',
        },
        background: {
            default: '#fff',
            paper: '#fff',
        },
        text: {
            primary: '#212121',
            secondary: '#757575',
        },
    },
    typography: {
        fontFamily: 'Montserrat, Inter, Roboto, Arial, sans-serif',
        htmlFontSize: 10,
        fontSize: 14,
    },
    components: {
        // ========== Typography Component Defaults ==========
        MuiTypography: {
            defaultProps: {
                variantMapping: {
                    h1: 'h1',
                    h2: 'h2',
                    h3: 'h3',
                    h4: 'h4',
                    h5: 'h5',
                    h6: 'h6',
                    body1: 'p',
                    body2: 'span',
                    caption: 'small',
                },
            },
        },

        // ========== Paper ==========
        MuiPaper: {
            styleOverrides: {
                root: {
                    '&.alternative': {
                        borderRadius: '5px',
                    },
                },
            },
        },


        // ========== Buttons ==========
        MuiButton: {
            styleOverrides: {
                root: {                    
                    boxShadow: 'none',
                    fontSize: '1.6rem',
                    fontWeight: 400,
                    textTransform: 'none',
                    padding: '1rem 2.4rem',
                    borderRadius: '50px',
                    transition: 'all 0.3s ease',
                    letterSpacing: '0.02em',
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                    textRendering: 'optimizeLegibility',
                    '&:hover': {
                        backgroundColor: '#292011',
                    },
                },
                sizeSmall: {
                    fontSize: '1.4rem',
                    padding: '0.5rem 1.6rem',
                },
                sizeMedium: {
                    fontSize: '1.6rem',
                    padding: '1rem 2.4rem',
                },
                sizeLarge: {
                    fontSize: '1.8rem',
                    padding: '1.4rem 3.2rem',
                },
                text: {
                    padding: '0',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    transformOrigin: 'center',
                    backfaceVisibility: 'hidden',
                    transition: 'transform 0.3s ease',

                    '&:hover': {
                        backgroundColor: 'transparent',                            
                        transition: 'transform 0.3s ease',
                        boxShadow: 'none',
                        borderRadius: '0',
                        WebkitFontSmoothing: 'antialiased',
                    },
                    '&.MuiButton-textPrimary': {
                        color: '#404040', 
                        padding: '0',
                        
                        '&:hover': {
                            color: '#333333',
                        },
                    },
                    '&.MuiButton-textSizeSmall': {
                        fontSize: '1rem',
                    },
                    '&.MuiButton-textSizeMedium': {
                        fontSize: '1.2rem',
                    },
                    '&.MuiButton-textSizeLarge': {
                        fontSize: '1.4rem',
                    },
                    '& .MuiButton-icon': {
                        fontSize: '1.2rem',
                    },
                },
                contained: {
                    '&.MuiButton-containedPrimary': {
                        backgroundColor: '#392C18',
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        borderColor: '#392C18',
                        padding: '1.2rem 2.4rem',
                        color: 'rgba(255, 255, 255, 0.85)',
                        '&:hover': {
                            backgroundColor: '#291f11',
                        },
                        '&:active': {
                            backgroundColor: '#291f11',
                        },
                    },
                },
                outlined: {
                    '&.MuiButton-outlinedPrimary': {
                        padding: '1.2rem 2.4rem',
                        backgroundColor: 'transparent',
                        
                        '&:hover': {
                            backgroundColor: 'transparent',
                        },
                        '&:active': {
                            backgroundColor: 'transparent',
                        },
                    },
                },
            },
        },

        // ========== Grid2 ==========
        MuiGrid2: {
            styleOverrides: {
                root: {
                    '--Grid-rowSpacing': '2rem',
                    '--Grid-columnSpacing': '1.5rem',
                },
            },
        },

        // ========== Form Controls / TextField / OutlinedInput ==========
        MuiFormControl: {
            styleOverrides: {
                root: {
                    display: 'block',

                    '&.centered .MuiInputBase-input': {
                        textAlign: 'center',
                        fontSize: '1.6rem',
                        lineHeight: '1',
                    },
                    '&.slider-field .MuiInputBase-sizeSmall.MuiInputBase-root': {
                        borderRadius: '5px !important',
                    },
                    '&.slider-field .MuiInputBase-sizeSmall .MuiInputBase-input': {
                        paddingLeft: '1rem',
                    },
                    '.MuiFormControlLabel-label': {
                        fontSize: '1.4rem'
                    },
                },
            },
        },

        MuiInputLabel: {
            styleOverrides: {
                root: {
                    left: '1rem',
                    color: 'rgba(0, 0, 0, 0.54)',
                },
            },
        },

        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: '50px',
                    fontSize: '1.6rem',

                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--textfield-border-color)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#d8d6d1',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#b1afaa',
                        borderWidth: '1px',
                    },
                    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#b6b6b6',
                    },
                    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#d65151',
                    },
                    '.alternative &': {
                        borderRadius:'5px',
                    },
                },
                input: {
                    padding: '1.5rem 2.4rem',
                    fontSize: '1.6rem',
                    height: 'auto',
                    '&::placeholder': {
                        color: 'var(--textfield-text-placeholder-color, #888682)',
                        opacity: 1,
                        fontWeight: '300',
                    },
                },
                inputSizeSmall: {
                    padding: '1rem 1rem',
                    fontSize: '1.4rem',
                    '&::placeholder': {
                        color: 'var(--textfield-text-placeholder-color, #888682)',
                        opacity: 1,
                        fontWeight: '300',
                    },
                },
                sizeSmall: {
                    borderRadius: '5px',
                },
                notchedOutline: {
                    legend: {
                        paddingRight: '0',
                        marginRight: '0',
                    },
                },
            },
        },

        MuiTextField: {
            styleOverrides: {
                root: ({ theme }) => ({
                    transition: 'all 0.5s ease',
                    marginBottom: '0 !important',
                    marginTop: '0',
                    overflow: 'visible !important',

                    '& label': {
                        color: '#888682',
                        fontSize: '1.4rem',
                        letterSpacing: '0.05em',
                        fontWeight: '300',
                        transition: 'all 0.3s ease',

                        '&.MuiInputLabel-shrink': {
                            top: '2px',
                        },
                        '&.MuiFormLabel-filled ~ .MuiOutlinedInput-root': {
                            boxShadow: 'var(--textfield-bg-autofill-box-shadow)',
                            borderTopLeftRadius: '50px',
                            borderBottomLeftRadius: '50px',
                            '& input': {
                                color: 'var(--textfield-text-filled-color)',
                                fontWeight:'600',
                            },
                        },
                    },

                    '.alternative &': {
                        borderRadius: '5px',
                    },
                    '& .MuiInputBase-sizeSmall input': {
                        paddingLeft: '2rem',
                    },
                    '& .MuiInputLabel-sizeSmall': {
                        paddingTop: '2px',
                    },

                    '& .MuiOutlinedInput-root': {
                        backgroundColor: 'var(--textfield-bg-color)',
                        transition: 'all 0.3s ease',
                        padding: '0',
                        color: 'var(--textfield-text-filled-color)',

                        '& input': {
                            backgroundColor: 'transparent',
                            color: 'var(--textfield-text-filled-color)',
                            transition: 'all 0.3s ease',

                            '&::placeholder': {
                                color: 'var(--textfield-text-placeholder-color)',
                                opacity: 0.6, 
                            },
                            '&:-webkit-autofill': {
                                WebkitBoxShadow: 'var(--textfield-bg-autofill-box-shadow)',
                                WebkitTextFillColor: 'var(--textfield-bg-autofill-text-color)',
                                transition: 'background-color 5000s ease-in-out 0s',
                            },
                        },
                        '& fieldset': {
                            borderColor: 'var(--textfield-border-color)',
                            transition: 'all 0.3s ease',
                        },
                        '&:hover fieldset': {
                            borderColor: '#353535',
                        },
                        '&.Mui-focused fieldset': {
                            borderWidth: '1px',
                            borderColor: 'rgba(0, 0, 0, 0.8)',
                        },
                        '&.Mui-error': {
                            '&:hover, &:focus': {
                                '& fieldset': {
                                    borderColor: '#d32f2f',
                                },
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#d32f2f',
                            },
                        },
                    },
                    [theme.breakpoints.up('sm')]: {
                        '& .MuiOutlinedInput-root input': {
                            fontSize: '1.4rem',
                        },
                    },
                }),
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    '& .MuiFormLabel-asterisk': {
                        color: '#d32f2f',
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    padding: 0,
                    fontSize: '1.6rem',

                    '.centered &': {
                        textAlign: 'center',
                    }
                },
                input: {
                    '&::placeholder': {
                        color: 'var(--textfield-text-placeholder-color, #888682)',
                        opacity: 1,
                    },
                    fontWeight: '600',
                },
                inputMultiline: {
                    fontWeight: '600',
                },
                inputSizeSmall: {
                    '&::placeholder': {
                        color: 'var(--textfield-text-placeholder-color, #888682)',
                        opacity: 1,
                    },
                },
            },
        },
        MuiList: {
            styleOverrides: {
                root: {
                    padding: '0',
                },
            },
        },
        MuiListSubheader: {
            styleOverrides: {
                root: {
                    '&.MuiListSubheader-gutters': {
                        pointerEvents: 'none',
                        backgroundColor: '#f0f0f0',
                        color: '#2b2823',
                        fontSize: '1.2rem',
                        fontWeight: '500',
                        textTransform: 'uppercase',
                    },
                },
            },
        },
        // ========== Select / Menu / MenuItem Overrides ==========
        MuiSelect: {
            defaultProps: {
                IconComponent: ExpandMoreIcon,
                autoWidth: false,
            },
            styleOverrides: {
                root: {
                    borderRadius: '50px',
                    backgroundColor: 'var(--textfield-bg-color)',
                    padding: '0',
                    fontSize: '1.4rem',
                    color: 'var(--textfield-arrow-color)',
                    transition: 'all 0.3s ease',
                    boxShadow: 'none',
                    margin: '0',
                    borderWidth: '0',
                    display: 'inline-flex',
                    width: '100%',
                    minWidth: 'unset',
 
                    '&:hover': {
                        boxShadow: 'none',
                        backgroundColor: '#f9f9f9',
                    },
                    '&.Mui-focused': {
                        boxShadow: 'none',
                        backgroundColor: '#fff',
                        color: '#392C18',
                        borderColor: '#b1afaa',
                        borderWidth: '1px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        borderColor: '#F0EDE8', //var(--textfield-border-color)

                        '&:hover': {
                            borderColor: '#d8d6d1', //var(--textfield-border-color-hover)
                        },
                        '&.Mui-focused': {
                            borderColor: '#b1afaa', //var(--textfield-border-color-focus)
                        },
                        '&.Mui-active': {
                            borderColor: '#b1afaa ', //var(--textfield-border-color-focus)
                        },
                    },
                    '& .MuiOutlinedInput-root': {
                        width: '100%',
                        '& .MuiSelect-select': {
                            paddingRight: '50px',
                        },
                    },
                    '&.alternative': {                       
                        borderRadius: '5px',               
                    },
                    '& .MuiSelect-nativeInput:not([value=""]) ~ .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e4e1dc', //var(--textfield-border-color-filled)
                    },
                },
                select: {
                    backgroundColor: 'var(--textfield-bg-color)',
                    boxShadow: 'none',
                    borderRadius: 'inherit',
                    padding: '1.5rem 4.5rem 1.5rem 2.5rem !important',
                    fontSize: '1.4rem',
                    width: '100%',
                    minWidth: 'unset',
                    '&:focus': {
                        backgroundColor: 'var(--color-bg-default)',
                    },
                    '.alternative &': {
                        padding: '1.2rem 4rem 1.2rem 2rem !important',
                    },
                    '& span': {
                        color: 'var(--textfield-text-placeholder-color, #888682)',
                    },
                },
                icon: {
                    color: '#8a7852',
                    fontSize: '2rem',
                    right: '1.5rem',
                    pointerEvents: 'none',
                },
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    '&.MuiInputAdornment-positionEnd': {
                        marginRight:'2rem',
                        paddingLeft:'2rem',
                        borderLeftWidth: '1px',
                        borderLeftStyle: 'solid',
                        borderColor: 'rgba(0, 0, 0, 0.12)',

                        '&.MuiInputAdornment-sizeSmall': {
                            marginRight:'1rem',
                            paddingLeft:'1.2rem',
                        },

                        '& .MuiButtonBase-root': {
                            marginRight: '1rem',
                            padding: '0',

                            '& .MuiSvgIcon-root': {
                                marginRight: '0',
                            },
                            '&.MuiIconButton-root': {
                                fill: '#888682',
                            },
                            '&.MuiIconButton-root:hover': {
                                backgroundColor: 'transparent',

                                '& .MuiSvgIcon-root': {
                                    fill: '#666666',
                                }
                            }
                        },
                        '& .MuiSvgIcon-root': {
                            marginRight: '1rem',
                            transition: 'fill 0.3s ease',
                            fill: '#888682',
                        }
                    },

                }
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                    borderRadius: '1.5rem',
                    maxHeight: '300px',
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: '#888',
                        borderRadius: '4px',
                        '&:hover': {
                            background: '#555',
                        },
                    },
                },
                list: {
                    padding: 0,
                    width: '100%',
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontSize: '1.4rem',
                    color: '#392C18',
                    padding: '12px 20px',
                    minHeight: 'auto',

                    '&:hover': {
                        backgroundColor: '#f7f7f7',
                    },
                    '&.Mui-selected': {
                        backgroundColor: '#B9A170',
                        color: '#FFF',
                        fontWeight: 500,
                        
                        '&:hover': {
                            backgroundColor: '#a69060',
                        },
                    },
                    '&.Mui-focusVisible': {
                        backgroundColor: '#B9A170',
                    },
                    '&.Mui-disabled': {
                        opacity: 0.5,
                    },
                },
            },
        },

        // ========== Stepper Overrides ==========
        MuiStepper: {
            styleOverrides: {
                root: { display: 'inline-flex' },
            },
        },
        MuiStepConnector: {
            styleOverrides: {
                root: { flex: '0 0 2rem' },
                line: { borderColor: '#ececec' },
            },
        },
        MuiStep: {
            styleOverrides: {
                root: ({ theme }) => ({
                    padding: '0',

                    [theme.breakpoints.up('md')]: {
                        padding: '0 .7rem',
                    },
                    [theme.breakpoints.up('lg')]: {
                        padding: '0 1.5rem', 
                    },
                }),
            },
        },
        MuiStepLabel: {
            styleOverrides: {
                label: ({ theme }) => ({
                    fontSize: "1rem",
                    whiteSpace: "nowrap",
                    "& a": {
                        fontWeight:500,
                        color: "#000",
                        opacity:'0.8',
                    },
                    "& a:hover": {
                        opacity:'1',
                        textDecoration: "none",
                    },
                    "&.Mui-active a": {
                        color: "rgba(0,0,0,0.8)",
                        fontWeight: "600",
                        opacity:'1',
                    },
                    "&.Mui-completed a": {
                        color: "rgba(0,0,0,0.7)",
                        opacity:'1',
                    },
                    "&.Mui-completed.Mui-active a": {
                        color: "rgba(0,0,0,0.9)",
                    },
                    [theme.breakpoints.up('md')]: {
                        fontSize: "1.1rem",
                    },
                }),
                iconContainer: ({ theme }) => ({
                    paddingRight: "0",
                    [theme.breakpoints.up('md')]: {
                        paddingRight: "1rem",
                    },
                }),
            },
        },
        MuiStepIcon: {
            styleOverrides: {
                root: {
                    color: '#d9d9d9',
                    '&.Mui-active': {
                        color: '#F07C29',
                    },
                    '&.Mui-completed': {
                        color: '#F07C29',
                    },
                    '&.Mui-completed svg': {
                        stroke: '#F07C29',
                        fill: 'none',
                        strokeWidth: '4px',
                    },
                },
            },
        },

        // ========== Slider ==========
        MuiSlider: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: '#404040',
                    height: '0.4rem',
                    
                    [theme.breakpoints.up('md')]: {
                        height: '0.6rem',
                    },
                }),
                thumb: ({ theme }) => ({
                    width: '3rem',
                    height: '3rem',
                    backgroundColor: '#ffff',
                    border: '0.1rem solid #f7f7f7',
                    boxShadow: '0 0.2rem 0.4rem rgba(0, 0, 0, 0.2)',
                    
                    '&:hover, &.Mui-focusVisible': {
                        boxShadow: '0 0.3rem 0.6rem rgba(0, 0, 0, 0.3)',
                    },
                    '&.Mui-active': {
                        backgroundColor: '#fff',
                        boxShadow: '0 0.3rem 1rem rgba(0, 0, 0, 0.3)',
                    },
                    
                    [theme.breakpoints.up('lg')]: {
                        width: '1.6rem',
                        height: '1.6rem',
                    },
                }),
                track: ({ theme }) => ({
                    height: '0.4rem',
                    border: 'none',
                    
                    [theme.breakpoints.up('md')]: {
                        height: '0.6rem',
                    },
                }),
                rail: ({ theme }) => ({
                    height: '0.4rem',
                    opacity: 0.5,
                    backgroundColor: '#ddd',
                    
                    [theme.breakpoints.up('md')]: {
                        height: '0.6rem',
                    },
                }),
                valueLabel: ({ theme }) => ({
                    backgroundColor: '#404040',
                    color: '#fff',
                    fontSize: '1.2rem',
                    fontWeight: 600,
                    padding: '0.4rem 0.8rem',
                    borderRadius: '0.4rem',
                    
                    '&:before': {
                        borderTopColor: '#404040',
                    },
                    
                    [theme.breakpoints.up('md')]: {
                        fontSize: '1.1rem',
                        padding: '0.3rem 0.6rem',
                    },
                }),
            },
        },
        // ========== Tabs ==========
        MuiTabs: {
            styleOverrides: {
                root: ({ theme }) => ({
                    backgroundColor: '#fff',
                    borderRadius: '5px',
                    fontSize: '1.3rem',
                    fontWeight: 500,
                    color:'rgba(0,0,0,0.8)',
                    padding: '.3rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',

                    [theme.breakpoints.up('md')]: {
                        fontSize: '1.4rem',
                    },
                    '& .Mui-selected': {
                        color:'#000',
                        borderBottom: '1px solid black',
                    },
                    '& .MuiTabs-indicator': {
                        border:0,
                        height:'100%',
                        backgroundColor: '#1c1c1c',
                        zIndex: 1,
                        borderRadius: '.5rem', 
                    },
                    '& .MuiTabScrollButton-root': {
                        width: '3rem',

                        '&:first-of-type': {
                            padding: '0',
                            justifyContent: 'flex-start',
                        },
                        '&:last-child': {
                            padding: '0',
                            justifyContent: 'flex-end',
                        },
                        '&.Mui-disabled': {
                            width: '0',
                        },
                    },
                    '& .MuiButtonBase-root': {
                        backgroundColor: 'transparent',
                        fontSize: '1.2rem',
                        position: 'relative',
                        zIndex: 2,
                        borderRadius: '.5rem',
                        flexGrow: 1,
                        border: '0',
                        padding:'1.2rem 1.3rem',
                        color: '#1c1c1c',
                        textRendering: 'geometricPrecision',
                        webkitFontSmoothing: 'antialiased',
                        MozOsxFontSmoothing: 'grayscale',

                        '&.Mui-selected': {
                            color: '#fff',
                            border: '0',
                        },
                    },
                }),
            },
        },
        // ========== Table, Chip, Checkbox, Radio etc. ==========
        MuiTableContainer: {
            styleOverrides: {
                root: {
                    marginTop: '16px',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: ({ theme }) => ({
                    marginRight: '4px',
                    backgroundColor: '#171717',
                    color: 'rgba(255,255,255,0.85)',
                    fontSize: '1rem',
                    height: '2.4rem',
                    
                    [theme.breakpoints.up('md')]: {
                        fontSize: '1.2rem',
                        height: '2.8rem',
                        marginRight: '0.25rem',
                    },

                    '& .MuiChip-deleteIcon': {
                        transition: 'color .3s ease',
                        color: 'rgba(255, 255, 255, 0.8)',
                        width: '0.8em',
                        height: '0.8em',
                        

                        '&:hover': {
                            color: 'rgba(255, 255, 255, 1)',
                        },
                    },
                    '& .MuiChip-label': {
                        paddingRight:'1.6rem',
                    },
                    
                    '&.colored-variant': {                        
                        padding:'.5rem .8rem',
                        fontSize:'1.3rem',
                        borderRadius:'3px',
                        boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.07)',                        
                        backgroundColor:'#f7f7f7',
                        height: 'auto',

                        '&:last-of-type': {
                            marginRight: '0',
                        },

                        '& .MuiChip-label': {
                            color: 'rgba(0, 0, 0, 0.5)', 
                            lineHeight: '1',
                            padding:0,
                            fontSize:'1.2rem',
                        },
                        '&.MuiChip-sizeSmall': {
                            padding:'.5rem',
                            fontSize:'1rem',
                        },    
                        '& .MuiChip-deleteIcon': {         
                            paddingTop:'0.1rem',                   
                            color: 'rgba(64,64,64,0.6)', 
                            marginLeft:'0.4rem',
                            marginRight:'0',

                            '&:hover': {
                                color: 'rgba(64,64,64,0.9)', 
                            },
                        },                    
                        '&.tag-familie': {
                            backgroundColor: '#F1FFF2',
                        },
                        '&.tag-ven, &.tag-friend': {
                            backgroundColor: '#F1F8FF',
                        },
                        '&.tag-kollega, &.tag-colleague': {
                            backgroundColor: '#FFF1F1',
                        },
                        '&.tag-vip': {
                            backgroundColor: '#FFFCF1',
                        },
                    },
                }),
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    fill: 'rgba(0, 0, 0, 0.8)',
                    transform: 'scale(1.1)',

                    '.MuiSvgIcon-root': {
                        borderRadius: '50%',
                        fill: 'rgba(0, 0, 0, 0.8)',
                        fontSize: 24,
                    },
                    '&.Mui-checked .MuiSvgIcon-root': {
                        fill: 'rgba(0, 0, 0, 0.8)',
                    },
                },
            },
        },

        MuiRadio: {
            styleOverrides: {
                root: {
                    '&:not(.Mui-checked)': {
                        color: 'rgba(0, 0, 0, 0.3)',
                        
                        '& svg[data-testid="RadioButtonCheckedIcon"]': {
                            opacity: 0,
                            display: 'none',
                        },
                    },
                    
                    '&.Mui-checked': {
                        color: '#b4a997',
                        
                        '& svg[data-testid="RadioButtonUncheckedIcon"]': {
                            opacity: 1,
                        },
                        '& svg[data-testid="RadioButtonCheckedIcon"]': {
                            opacity: 1,
                        },
                    },
                    
                    '.MuiSvgIcon-root': {
                        borderRadius: '50%',
                        fill: 'rgba(0, 0, 0, 0.8)',
                        fontSize: 24,
                    },
                    '&.Mui-checked .MuiSvgIcon-root': {
                        fill: 'rgba(0, 0, 0, 0.8)',
                    },
                    '&.rsvp-status': {
                        '&.Mui-checked .MuiSvgIcon-root': {
                            fill: 'var(--rsvp-color, #000) !important',
                        },
                    },
                    '.MuiFormControlLabel-label.Mui-disabled': {
                        color: 'var(--rsvp-color, #000) !important',
                    },
                    '&.Mui-disabled': {
                        color: 'var(--rsvp-color, #000) !important',
                    },
                },
            },
        },
        // ========== ToggleButton, etc. ==========

        MuiButtonGroup: {
            styleOverrides: {
                root: {
                    [`.alt-button-group &`]: {
                        '& .MuiButtonGroup-firstButton': {
                            borderTopLeftRadius: '3px',
                            borderBottomLeftRadius: '3px',
                        },
                        '& .MuiButtonGroup-lastButton': {
                            borderTopRightRadius: '3px',
                            borderBottomRightRadius: '3px',
                        },
                        '& .MuiButtonBase-root': {
                            padding: '0.6rem .8rem',

                            '& svg': {
                                width: '2.1rem',
                            },
                            '&.Mui-selected': {
                                backgroundColor: '#1c1c1c',
                                color: '#fff',
                                '&:hover': {
                                    backgroundColor: '#121212',
                                },
                            },
                        },
                    },
                }
            }
        },
        MuiToggleButtonGroup: {
            styleOverrides: {
                root: {
                    borderRadius: '.8rem',
                    backgroundColor: '#fff',
                    '& .MuiToggleButtonGroup-firstButton': {
                        borderTopLeftRadius: '.8rem',
                        borderBottomLeftRadius: '.8rem',
                    },
                    '& .MuiToggleButtonGroup-lastButton': {
                        borderTopRightRadius: '.8rem',
                        borderBottomRightRadius: '.8rem',
                    },

                    [`.alt-toggle-button-group &`]: {
                        borderLeftWidth: '1px',

                        '& .MuiToggleButtonGroup-firstButton': {
                            borderRadius: '3px',
                        },
                        '& .MuiToggleButtonGroup-middleButton': {
                            borderRadius: '3px',
                            margin: '0',
                            border: '0',

                        },
                        '& .MuiToggleButtonGroup-lastButton': {
                            borderRadius: '3px',

                        },
                        '& .MuiButtonBase-root': {
                            backgroundColor: 'var(--textfield-bg-color)',
                            marginRight: '5px',
                            padding: '1.2rem 1.4rem',
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            borderColor: 'var(--textfield-border-color)',
                            lineHeight: '1',

                            '& svg': {
                                width: '2.1rem',
                            },
                            '&.Mui-selected': {
                                backgroundColor: '#291f11',
                                border: '1px solid transparent',
                            },
                        },
                    },
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    textTransform: 'none',
                    fontSize: '1.4rem',
                    fontWeight: 400,
                    padding: '1.5rem',
                    borderRadius: 0,
                    color: '#404040',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    transition: 'background-color 0.3s ease',
                    [theme.breakpoints.up('md')]: {
                        padding: '2rem 2.5rem',
                    },
                    '&.Mui-selected': {
                        backgroundColor: '#404040',
                        color: '#fff',
                        '&:hover': {
                            backgroundColor: '#373737',
                        },
                        
                    },
                    '&.MuiToggleButtonGroup-firstButton': {
                        paddingLeft: '2rem',
                    },
                    '&.MuiToggleButtonGroup-lastButton': {
                        paddingRight: '2rem',
                    },
                }),
            },
        },

        // ========== Autocomplete, etc. ==========
        MuiAutocomplete: {
            styleOverrides: {
                root : {
                    '& .MuiAutocomplete-tag': {
                        margin: '12px 3px',

                        '&:first-of-type': {
                            marginLeft: '12px',
                        },
                    },
                    '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
                        padding: '1.5rem 2.4rem'
                    },
                    '& .MuiOutlinedInput-root .MuiAutocomplete-endAdornment': {
                        right:'1.5rem',
                    },
                },
                paper: {
                    borderRadius: '8px',
                    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                    backgroundColor: '#fff',
                },
                listbox: {
                    padding: '0',
                    '& .MuiAutocomplete-option': {
                        padding: '10px 20px',
                        fontSize: '1.4rem',
                        color: '#392C18',
                        '&[aria-selected="true"]': {
                            backgroundColor: '#f5f5f5',
                            color: '#8a7852',
                        },
                        '&:hover': {
                            backgroundColor: '#f9f9f9',
                        },
                    },
                },
            },
        },


        // ========== Modal, Popover, etc. ==========
        MuiModal: {
            defaultProps: {
              disableScrollLock: true,
            },
          },
          MuiPopover: {
            defaultProps: {
              disableScrollLock: true,
            },
          },
        // ========== Form Helper Text ==========

        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    backgroundColor: '#fff',
                    color: '#404040',
                    margin: '0',
                    display: 'block',
                    borderRadius: '1rem',
                    fontSize: '1rem',
                    position: 'absolute',                   
                    right: '.2rem',
                    bottom: '-0.8rem',
                    lineHeight: '1',
                    
                    '&.Mui-error': { 
                        padding: '.4rem .8rem',  
                        color: 'white',
                        backgroundColor: '#d32f2f',   
                    },
                },
            },
        },  
        MuiSvgIcon: {
            styleOverrides: {
                root: {
                    fontSize: '2rem',
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    textAlign: 'center',
                },
                popper: {
                    zIndex: 1300,
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    '& .MuiTouchRipple-root': {
                        color: 'rgba(0, 0, 0, 0.2)',
                    },
                },
            },
        },
    },
}, daDK);

export default theme;
